// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OverflowMenuPresentationProps } from "@conklin/MobiusOverrides/OverflowMenu";
import { ProductDescriptionStyles } from "@conklin/Overrides/Components/ProductDescription";
import IconSearch from "@conklin/XDL/Icons/IconSearch";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { ellipsis, styledScrollbar, xScreen } from "@conklin/XDL/Utilities";
import color from "@conklin/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@conklin/XDL/Utilities/links";
import { mergeWithCss } from "@conklin/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldClickable } from "@insite/mobius/FormField";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { LinkPresentationProps, StyledClickable, StyledIcon, StyledTypography } from "@insite/mobius/Link";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import styled, { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui10",
        },
        text: {
            default: "text",
            hover: "common.ui1",
        },
    };

    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 12px;
                padding: 8px;
                line-height: 18px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)} !important;
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;
        box-sizing: border-box;

        @media print {
            display: block;
            padding-top: 12px;
            padding-bottom: 12px;
            float: left;
            width: 100%;
        }
    `,
};

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    size: "small" | "default" = "default",
    iconPosition: "left" | "right" = "left",
    isWhite = false,
    linkStyles?: LinkPresentationProps,
    typographyStyles?: TypographyPresentationProps,
    iconStyles?: IconPresentationProps,
) => {
    const marginPosition = (linkStyles?.icon?.position || iconPosition) === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: mergeWithCss(
                    {
                        size: size === "small" ? 12 : 16,
                        lineHeight: size === "small" ? "15px" : "19px",
                        weight: fontConfig.weight.bold,
                        css: css`
                            letter-spacing: 0;
                        `,
                    },
                    typographyStyles,
                ),
                icon: {
                    iconProps: mergeWithCss(
                        {
                            src: icon,
                            size: size === "small" ? 12 : 16,
                            css: css`
                                ${marginPosition}: 6px;
                                opacity: 0.5;

                                &,
                                & svg {
                                    width: auto;
                                }
                            `,
                        },
                        iconStyles,
                    ),
                    position: linkStyles?.icon?.position || iconPosition,
                },
                css: css`
                    && {
                        ${StyledTypography}, ${StyledIcon} {
                            color: ${isWhite ? color("common.ui1") : color("common.ui9")};
                        }

                        &:not([disabled]):hover {
                            ${StyledTypography}, ${StyledIcon} {
                                color: ${isWhite ? color("common.ui1") : color("common.ui10")};
                            }

                            ${isWhite &&
                            css`
                                ${StyledTypography} {
                                    text-decoration: underline;
                                }
                            `}
                        }

                        &[disabled] {
                            ${StyledTypography} {
                                opacity: 0.33;
                                cursor: not-allowed;
                            }

                            ${StyledIcon} {
                                opacity: 0.16;
                                cursor: not-allowed;
                            }
                        }
                    }
                `,
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...iconButtonCommonStyles(
            iconSrc,
            12,
            {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        line-height: 12px;
                    `,
                },
            },
            undefined,
            {
                css: css`
                    margin: 0 10px;
                    display: flex;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            margin-top: 17px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    submitButton: {
        variant: "primary",
    },
};

export const searchBoxFieldClickableStyles: TextFieldProps = {
    iconProps: {
        src: IconSearch,
    },
    cssOverrides: {
        formInputWrapper: css`
            ${FormFieldClickable} {
                background-color: transparent;

                &:disabled ${IconWrapper} {
                    color: ${color("common.disabled")};
                }
            }
        `,
    },
};

export const CenteringWrapper = styled(StyledWrapper)`
    height: 500px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${LoadingSpinnerStyle} {
        margin: auto;
    }
`;

export const loadingText: TypographyProps = {
    weight: fontConfig.weight.bold,
    css: css`
        display: inline-block;
        text-align: left;
        min-width: 22px;

        &::after {
            overflow: hidden;
            display: inline-block;
            vertical-align: bottom;
            animation: ellipsis steps(4, end) 700ms infinite 1s;
            content: "\\2026";
            width: 0;
            color: ${color("common.text")};
        }

        @keyframes ellipsis {
            to {
                width: 1.25em;
            }
        }
    `,
};

export const sharedProductDescriptionStyles = (lines = 2, sm?: number): ProductDescriptionStyles => {
    let smLines = sm;
    if (!smLines) {
        smLines = lines;
    }
    return {
        productDetailDescription: {
            size: 14,
            color: "text",
            weight: "bold",
            css: css`
                display: block;
                letter-spacing: 0;
                line-height: 16px;
                margin-bottom: 8px;
                ${ellipsis(lines)}
                ${xScreen("lte", "sm")} {
                    ${ellipsis(smLines)}
                }
                @media print {
                    display: block;
                    width: 100%;
                    text-align: start;
                }
            `,
        },
        productDetailLink: {
            color: "text",
            typographyProps: {
                size: 14,
                color: "text",
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 16px;
                    ${ellipsis(lines)}
                    ${xScreen("lte", "sm")} {
                        ${ellipsis(smLines)}
                    }
                `,
            },
            css: css`
                margin-bottom: 8px;
                align-items: flex-start;
                @media print {
                    display: inline-block;
                    width: 100%;
                    text-align: start;
                }
            `,
        },
    };
};

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}
export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 140,
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }
            `,
            menu: css`
                padding: 4px 0;
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,

            menuItem: css`
                padding: 8px 12px 0;
                font-size: 12px;
                line-height: 15px;
                color: ${color("common.ui9")};
                font-weight: bold;
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: 100%;
                    margin-top: 8px;
                }
            `,
        },
    },
    moreActionsLink: {
        icon: {
            iconProps: {
                src: ChevronDown,
                size: 14,
            },
            position: "right",
        },
        typographyProps: {
            size: 12,
            lineHeight: "15px",
        },
    },
};
